import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * On native platforms, Supabase auth tokens stored in `localStorage` live
 * inside the WebView's data dir, which is plain-text and (without backup
 * exclusion) gets uploaded to Google Drive Auto Backup.
 *
 * This module mirrors a small allowlist of localStorage keys into
 * `@capacitor/preferences`, which on Android maps to `EncryptedSharedPreferences`
 * (when available) and on iOS to the Keychain via the secure storage backing.
 *
 * Because `src/integrations/supabase/client.ts` is auto-generated and cannot
 * be edited, we transparently wrap `localStorage`'s setItem/removeItem so
 * that any auth write also persists securely, and seed `localStorage` from
 * Preferences at boot so existing sessions survive across app restarts even
 * if WebView storage is wiped.
 */

// Keys we want to keep in secure native storage. Supabase stores its session
// under `sb-<project-ref>-auth-token`.
const SECURE_KEY_PREFIXES = ['sb-', 'supabase.auth.'];

const isSecureKey = (key: string) =>
  SECURE_KEY_PREFIXES.some((p) => key.startsWith(p));

let initialized = false;

export const initSecureStorage = async (): Promise<void> => {
  if (initialized) return;
  initialized = true;

  if (!Capacitor.isNativePlatform() || typeof window === 'undefined') return;

  try {
    // 1. Seed localStorage from Preferences so a fresh WebView still sees
    //    the persisted auth session.
    const { keys } = await Preferences.keys();
    for (const key of keys) {
      if (!isSecureKey(key)) continue;
      if (window.localStorage.getItem(key) !== null) continue;
      const { value } = await Preferences.get({ key });
      if (value !== null) window.localStorage.setItem(key, value);
    }

    // 2. Patch localStorage so future writes mirror to Preferences and
    //    deletes propagate. Reads stay synchronous against localStorage.
    const origSet = window.localStorage.setItem.bind(window.localStorage);
    const origRemove = window.localStorage.removeItem.bind(window.localStorage);

    window.localStorage.setItem = (key: string, value: string) => {
      origSet(key, value);
      if (isSecureKey(key)) {
        // Fire and forget — Preferences writes are async.
        void Preferences.set({ key, value });
      }
    };

    window.localStorage.removeItem = (key: string) => {
      origRemove(key);
      if (isSecureKey(key)) {
        void Preferences.remove({ key });
      }
    };
  } catch (error) {
    // Non-fatal: fall back to plain localStorage behavior.
    console.warn('[secureStorage] init failed:', error);
  }
};